import { socialNetworkPrompts, socialNetworkPromptsSuffix } from "components/layout/MagicEditor/functions/AdaptToSocialNetwork";
import { Loading } from "components/shared";
import { Button, Dropdown, Notification, toast } from "components/ui";
import InputModern from "components/ui/Input/InputModern";
import { CalendarActiveSocialNetworkContext } from "contexts/CalendarActiveSocialNetworkContext";
import { CompanyContext } from "contexts/CompanyContext";
import { ModalContext } from "contexts/ModalContext";
import { AIContext } from "contexts/AIContext";
import { TrendingNewsContext } from "contexts/TrendingNewsContext";
import React, { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { HiChevronDown, HiOutlineMagnifyingGlass } from "react-icons/hi2";
import { getReadableByURL } from "utils/getReadableByURL";
import { useTrendingNews } from "utils/hooks/useTrendingNews";
import TrendingNewsItem from "views/home/TrendingNewsItem";
import ConfigPanel from "../ConfigPanel";

// Bing
const supportedLanguages = [
    {
        "language_code": "en-US", 
        "language_name": "United States (English)",
    },
    {
        "language_code": "es-US",
        "language_name": "United States (Spanish)",
    },
    {
        "language_code": "es-AR", 
        "language_name": "Argentina",
    },
    {
        "language_code": "en-AU", 
        "language_name": "Australia",
    },
    {
        "language_code": "de-AT", 
        "language_name": "Austria",
    },
    {
        "language_code": "nl-BE", 
        "language_name": "Belgium (Dutch)",
    },
    {
        "language_code": "fr-BE", 
        "language_name": "Belgium (French)",
    },
    {
        "language_code": "pt-BR", 
        "language_name": "Brazil",
    },
    {
        "language_code": "en-CA", 
        "language_name": "Canada (English)",
    },
    {
        "language_code": "fr-CA", 
        "language_name": "Canada (French)",
    },
    {
        "language_code": "es-CL", 
        "language_name": "Chile",
    },
    {
        "language_code": "da-DK", 
        "language_name": "Denmark",
    },
    {
        "language_code": "fi-FI", 
        "language_name": "Finland",
    },
    {
        "language_code": "fr-FR", 
        "language_name": "France",
    },
    {
        "language_code": "de-DE", 
        "language_name": "Germany",
    },
    {
        "language_code": "zh-HK", 
        "language_name": "China",
    },
    {
        "language_code": "en-IN", 
        "language_name": "India",
    },
    {
        "language_code": "en-ID", 
        "language_name": "Indonesia",
    },
    {
        "language_code": "it-IT", 
        "language_name": "Italy",
    },
    {
        "language_code": "ja-JP", 
        "language_name": "Japan",
    },
    {
        "language_code": "ko-KR", 
        "language_name": "Korea",
    },
    {
        "language_code": "en-MY", 
        "language_name": "Malaysia",
    },
    {
        "language_code": "es-MX", 
        "language_name": "Mexico",
    },
    {
        "language_code": "nl-NL", 
        "language_name": "Netherlands",
    },
    {
        "language_code": "en-NZ", 
        "language_name": "New Zealand",
    },
    {
        "language_code": "no-NO", 
        "language_name": "Norway",
    },
    {
        "language_code": "zh-CN", 
        "language_name": "People's republic of China",
    },
    {
        "language_code": "pl-PL", 
        "language_name": "Poland",
    },
    {
        "language_code": "en-PH", 
        "language_name": "Republic of the Philippines",
    },
    {
        "language_code": "ru-RU", 
        "language_name": "Russia",
    },
    {
        "language_code": "en-ZA", 
        "language_name": "South Africa",
    },
    {
        "language_code": "es-ES", 
        "language_name": "Spain",
    },
    {
        "language_code": "sv-SE", 
        "language_name": "Sweden",
    },
    {
        "language_code": "fr-CH", 
        "language_name": "Switzerland",
    },
    {
        "language_code": "de-CH", 
        "language_name": "Switzerland",
    },
    {
        "language_code": "zh-TW", 
        "language_name": "Taiwan",
    },
    {
        "language_code": "tr-TR", 
        "language_name": "Turkey",
    },
    {
        "language_code": "en-GB", 
        "language_name": "United Kingdom",
    }
];

const NewsTemplate = ({
    generatePostsFromContexts,
    config,
    setConfig,
    home_item,
}) => {

    const perPage = 21;
    const maxSelectedNews = 10;

    const { calendarActiveSocialNetwork } = useContext(CalendarActiveSocialNetworkContext);
    const { company } = useContext(CompanyContext);
    const { trendingNews } = useContext(TrendingNewsContext);
    const { getTrendingNews } = useTrendingNews();
    const { closeModal } = useContext(ModalContext);
    const [ query, setQuery ] = useState("");
    const [ dateRange, setDateRange ] = useState([
        new Date(new Date().getTime() - 30 * 3600 * 24 * 1000),
        new Date()
    ]);
    const [ sortBy, setSortBy ] = useState('publishedAt');
    const [ news, setNews ] = useState([]);
    const [ newsQtyShow, setNewsQtyShow ] = useState(perPage);
    const [ selectedNews, setSelectedNews ] = useState([]);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isLoadingReady, setIsLoadingReady ] = useState(false);
    const [ selectedLanguageCode, setSelectedLanguageCode ] = useState();
    const { t } = useTranslation();

    const sortByOptions = [
        { value: 'publishedAt', label: "Most recent" },
        { value: 'relevancy', label: "Relevancy" },
        // { value: 'popularity', label: "Popularity" },
    ];

    const languageOptions = supportedLanguages.map((item) => {
        return { value: item.language_code, label: item.language_name }
    });

    const selectedLanguageOption = languageOptions.find((option) => option.value === selectedLanguageCode) || languageOptions?.[0];

    const getNews = () => {
        setIsLoading(true);
        setNews([]);
        getTrendingNews(query, dateRange[0], dateRange[1], sortBy, selectedLanguageCode).then((response) => {
            setNews(response);
        }).finally(() => {  
            setIsLoading(false);
        });
    }

    const onSelect = (item) => {
        if (selectedNews?.length >= maxSelectedNews) {
            toast.push(<Notification title={`You can't select more than ${maxSelectedNews} news at once.`} type="warning" />);
            return;
        }
        setSelectedNews([item, ...selectedNews])
    }

    const onUnselect = (item) => {
        setSelectedNews(selectedNews.filter(e => e?.title != item?.title))
    }
    
    const handleOnSubmit = async () => {
        const response = await Promise.allSettled(selectedNews.map((news) => getReadableByURL(news?.url)));
        // Contexts and images come from the same read news, so a page that fails to load drops
        // its image too and images[i] stays the picture of contexts[i].
        const readIndexes = response.map((e, i) => !!e?.value ? i : -1).filter(i => i >= 0);
        const contexts = readIndexes.map((i) => {
            const article = response[i].value;
            const text = article?.textContent?.length > 3500 ? article?.textContent?.substring(0, 3500) : article?.textContent;
            return { source: { kind: 'article', content: `${article?.title || ''}\n${text || ''}`.trim() } };
        });
        const images = readIndexes.map((i) => selectedNews[i]?.urlToImage || null);
        generatePostsFromContexts(contexts, images);
    }

    useEffect(() => {
        if (news?.length == 0) {
            if (localStorage.getItem(`newsTemplate${company?.id}`)) {
                setNews(JSON.parse(localStorage.getItem(`newsTemplate${company?.id}`)));
                setQuery(localStorage.getItem(`newsTemplateQuery${company?.id}`));
                if (localStorage.getItem(`newsTemplateLanguageCode${company?.id}`)) {
                    setSelectedLanguageCode(localStorage.getItem(`newsTemplateLanguageCode${company?.id}`));
                }
            } else {
                setNews(trendingNews);
            }
        }
    }, []);

    useEffect(() => {
        if (news) {
            // localStorage.setItem(`newsTemplate${company?.id}`, JSON.stringify(news));
            localStorage.setItem(`newsTemplateQuery${company?.id}`, query);
            localStorage.setItem(`newsTemplateLanguageCode${company?.id}`, selectedLanguageCode);
        }
    }, [news]);

    useEffect(() => {
        if (home_item) {
            onSelect(home_item);
        }
    }, []);

    useEffect(() => {
        const defaultLanguage = supportedLanguages?.filter(e => e.language_code.toLocaleLowerCase().includes(company?.language_iso_code?.toLocaleLowerCase()))?.[0]?.language_code || supportedLanguages?.[0]?.language_code;
        setSelectedLanguageCode(defaultLanguage);
    }, [company]);

    return (
        <div className="w-full h-full flex items-start p-4 gap-4">
            <ConfigPanel 
                selected={selectedNews}
                onSubmit={handleOnSubmit}
                config={config}
                setConfig={setConfig}
                template="news"
            />
            <div className="flex-1 flex flex-col lg:flex-row gap-2 w-full h-full overflow-y-auto">
                <div className="overflow-y-auto p-1 shrink-0 flex-1 flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-col lg:flex-row items-center gap-2 w-full">
                            <Dropdown
                                placement="bottom-start"
                                parentClassName={`w-full flex-1 w-full`}
                                menuClass="w-auto max-h-[300px] overflow-y-auto"
                                renderTitle={
                                    <Button className={`w-full flex gap-2 justify-between items-center !py-4 !px-6 !h-auto`}>
                                        <div className="flex items-center gap-2">
                                            <span className="!font-normal">Country:</span>
                                            <img className="h-3 w-4 object-fit object-cover" src={`https://flagcdn.com/h40/${selectedLanguageOption?.value?.split("-")?.[1]?.toLocaleLowerCase()}.png`} />
                                            {selectedLanguageOption?.label}
                                        </div>
                                        <HiChevronDown />
                                    </Button>
                                }
                            >
                                {languageOptions.map((language, key) => (
                                    <Dropdown.Item onClick={() => setSelectedLanguageCode(language.value)} className="flex items-center gap-2 py-2 px-4">
                                        <img className="h-3 w-4 object-fit object-cover" src={`https://flagcdn.com/h40/${language.value.split("-")?.[1]?.toLocaleLowerCase()}.png`} />
                                        <span>{language.label}</span>
                                    </Dropdown.Item>
                                ))}
                            </Dropdown>
                            <Dropdown
                                placement="bottom-start"
                                parentClassName={`w-full flex-1 w-full`}
                                menuClass="w-auto max-h-[300px] overflow-y-auto"
                                renderTitle={
                                    <Button className={`w-full flex gap-2 justify-between items-center !py-4 !px-6 !h-auto`}>
                                        <div className="flex items-center gap-2">
                                            <span className="!font-normal">Sort by:</span>
                                            {sortByOptions.find((option) => option.value === sortBy).label}
                                        </div>
                                        <HiChevronDown />
                                    </Button>
                                }
                            >
                                {sortByOptions.map((sort, key) => (
                                    <Dropdown.Item onClick={() => setSortBy(sort.value)} className="flex items-center gap-2 py-2 px-4">
                                        <span>{sort.label}</span>
                                    </Dropdown.Item>
                                ))}
                            </Dropdown>
                        </div>
                        <div className='relative bg-secondary-500 dark:bg-gray-700 rounded-2xl'> 
                            <InputModern 
                                fieldsetClassName="!bg-transparent"
                                className="!pl-8 !px-5 !py-5 !h-auto rounded-2xl !bg-transparent !font-semibold !text-slate-800 dark:!text-slate-200" 
                                value={query}
                                placeholder={t(`newsTemplate.placeholderQuery`)} 
                                prefix={<span className="!font-normal text-sm text-tertiary-600 dark:text-gray-200 mr-2 ml-2 !mb-2.5 inline-block">Topic: </span>}
                                onChange={(e) => setQuery(e.target.value)} 
                                onKeyUp={(e) => e.key == 'Enter' ? getNews() : void 0}
                            />
                            <Button 
                                loading={isLoading}
                                variant="twoTone" 
                                className="flex gap-2 items-center !absolute right-2.5 top-1/2 translate-y-[-50%]"
                                onClick={() => getNews()}
                                icon={<HiOutlineMagnifyingGlass />}
                            />
                        </div>
                    </div>

                    <div className="h-fit flex flex-col sm:grid sm:grid-cols-2 md:grid-cols-3 lg-cols-3 gap-4 pb-8">
                        {isLoading && 
                            <div className="col-span-3 flex items-center justify-center">
                                <Loading type={'element'} />
                            </div>
                        }
                        {news && news?.slice(0, newsQtyShow).map((item, key) => (
                            <TrendingNewsItem 
                                key={key} 
                                news={item} 
                                hasDescription={false}
                                height={160}
                                isSelect={true} 
                                selected={selectedNews.filter(e => e?.title == item?.title)?.length > 0} 
                                onSelect={() => onSelect(item)} 
                                onUnselect={() => onUnselect(item)} 
                                className={`news-index-${key} !shadow-none !bg-secondary-500 dark:!bg-gray-800`}
                            />
                        ))}
                        {newsQtyShow < news?.length && 
                            <div className="col-span-3 flex items-center justify-center mt-4">
                                <Button loading={isLoading} onClick={() => setNewsQtyShow(newsQtyShow + perPage)}>
                                    <Trans i18nKey={`newsTemplate.loadMore`}>Load more</Trans>
                                </Button>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

export default NewsTemplate;